/**
 * DirectReportModal Component
 * Modal/slide-over showing detailed performance view for a direct report
 */

import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, User, TrendingUp, TrendingDown, Minus, FileDown } from 'lucide-react';
import { clsx } from 'clsx';
import { PerformanceContributors } from '../PerformanceContributors';
import { OKRPerformanceTable } from '../OKRPerformanceTable';
import { PerformanceSummaryText } from '../PerformanceSummaryText';
import type { PerformanceData, PerformanceFactor, OKRPerformanceItem } from '../../../types';
import { getScoreColor } from '../../../types';

export interface DirectReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  userEmail?: string;
  avatarUrl?: string;
  performanceData?: PerformanceData;
  previousScore?: number;
  isLoading?: boolean;
  onExportPDF?: (userId: string) => void;
  onOKRClick?: (okrId: string) => void;
}

export const DirectReportModal: React.FC<DirectReportModalProps> = ({
  isOpen,
  onClose,
  userName,
  userEmail,
  avatarUrl,
  performanceData,
  previousScore,
  isLoading = false,
  onExportPDF,
  onOKRClick,
}) => {
  const score = performanceData?.score.overall ?? 0;
  const trend = previousScore !== undefined ? score - previousScore : 0;

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-stone-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-2xl">
                  <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          {avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt={userName}
                              className="w-16 h-16 rounded-full object-cover border-2 border-white"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center border-2 border-white/50">
                              <User className="w-8 h-8 text-white" />
                            </div>
                          )}
                          <div>
                            <Dialog.Title className="text-xl font-semibold text-white">
                              {userName}
                            </Dialog.Title>
                            {userEmail && (
                              <p className="text-sm text-blue-100">{userEmail}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <span
                                className="text-2xl font-bold text-white"
                              >
                                {Math.round(score)}
                              </span>
                              <span className="text-sm text-blue-100">/100</span>
                              {trend !== 0 && (
                                <span
                                  className={clsx(
                                    'flex items-center gap-1 text-sm',
                                    trend > 0 ? 'text-green-300' : 'text-red-300'
                                  )}
                                >
                                  {trend > 0 ? (
                                    <TrendingUp className="w-4 h-4" />
                                  ) : (
                                    <TrendingDown className="w-4 h-4" />
                                  )}
                                  {trend > 0 ? '+' : ''}{trend.toFixed(1)}
                                </span>
                              )}
                              {trend === 0 && previousScore !== undefined && (
                                <Minus className="w-4 h-4 text-blue-200" />
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {onExportPDF && (
                            <button
                              onClick={() => onExportPDF(performanceData?.userId || '')}
                              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                              title="Export PDF"
                            >
                              <FileDown className="w-5 h-5" />
                            </button>
                          )}
                          <button
                            onClick={onClose}
                            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 px-6 py-6 space-y-6">
                      {isLoading ? (
                        <div className="animate-pulse space-y-6">
                          <div className="h-48 bg-stone-200 rounded-lg" />
                          <div className="h-64 bg-stone-200 rounded-lg" />
                          <div className="h-48 bg-stone-200 rounded-lg" />
                        </div>
                      ) : performanceData ? (
                        <>
                          {/* Summary */}
                          {performanceData.summary && (
                            <PerformanceSummaryText
                              summary={performanceData.summary}
                              userName={userName}
                              periodLabel={`${performanceData.period.type}`}
                            />
                          )}

                          {/* Score Breakdown */}
                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-stone-50 rounded-lg p-4">
                              <p className="text-sm text-stone-500 mb-1">OKR Progress</p>
                              <p
                                className="text-2xl font-bold"
                                style={{ color: getScoreColor(performanceData.score.okrProgress) }}
                              >
                                {Math.round(performanceData.score.okrProgress)}%
                              </p>
                            </div>
                            <div className="bg-stone-50 rounded-lg p-4">
                              <p className="text-sm text-stone-500 mb-1">Check-in Rate</p>
                              <p
                                className="text-2xl font-bold"
                                style={{ color: getScoreColor(performanceData.score.checkInCompliance) }}
                              >
                                {Math.round(performanceData.score.checkInCompliance)}%
                              </p>
                            </div>
                            <div className="bg-stone-50 rounded-lg p-4">
                              <p className="text-sm text-stone-500 mb-1">Engagement</p>
                              <p
                                className="text-2xl font-bold"
                                style={{ color: getScoreColor(performanceData.score.engagement) }}
                              >
                                {Math.round(performanceData.score.engagement)}
                              </p>
                            </div>
                            <div className="bg-stone-50 rounded-lg p-4">
                              <p className="text-sm text-stone-500 mb-1">Timeliness</p>
                              <p
                                className="text-2xl font-bold"
                                style={{ color: getScoreColor(performanceData.score.timeliness) }}
                              >
                                {Math.round(performanceData.score.timeliness)}
                              </p>
                            </div>
                          </div>

                          {/* Factor Breakdown */}
                          {performanceData.factors && performanceData.factors.length > 0 && (
                            <PerformanceContributors
                              factors={performanceData.factors as PerformanceFactor[]}
                              title="Score Factors"
                            />
                          )}

                          {/* OKR List */}
                          {performanceData.okrs && performanceData.okrs.length > 0 && (
                            <OKRPerformanceTable
                              okrs={performanceData.okrs as OKRPerformanceItem[]}
                              title="OKR Performance"
                              onOKRClick={onOKRClick}
                            />
                          )}
                        </>
                      ) : (
                        <div className="text-center py-12 text-stone-400">
                          <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No performance data available</p>
                        </div>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default DirectReportModal;
